import { cn } from '@/lib/utils'
import { Slot } from '@radix-ui/react-slot'
import { createContext, useContext, useRef } from 'react'
import { ErrorCard } from '@/bc'
import { Empty as EmptyCard } from '@/bc/empty'
import { useSize } from 'ahooks'
import { CardSkeleton } from '@/bc/skeleton/card-skeleton'

const ListContext = createContext<{ loading?: boolean; error?: Error; empty?: boolean }>(null!)
function useListContext() {
  return useContext(ListContext)
}
export interface GridListProps {
  loading?: boolean
  error?: Error
  empty?: boolean
  asChild?: boolean
  ref?: React.Ref<HTMLDivElement>
}
/**
 * 初步的List布局，目前用于商品中心-后续需要根据产品需求作调整
 */

function Root({
  children,
  className,
  style,
  loading,
  error,
  empty,
  asChild,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & GridListProps) {
  const $root = useRef<HTMLDivElement>(null)
  const Com = asChild ? Slot : 'div'
  return (
    <ListContext.Provider value={{ loading, error, empty }}>
      <Com ref={$root} {...rest} style={style} className={className}>
        {children}
      </Com>
    </ListContext.Provider>
  )
}
type GridListViewBoxProps = {
  gap?: [number, number]
  /**
   * @deprecated
   */
  cols?: number
  columnWidth?: number
} & React.HTMLAttributes<HTMLDivElement>
function ViewBox({ children, gap = [16, 16], cols, columnWidth = 255, style, ...rest }: GridListViewBoxProps) {
  const [columnGap, rowGap] = gap || []
  const $container = useRef<HTMLDivElement>(null)
  const size = useSize($container)
  const actualCols = cols ? cols : Math.floor((size?.width ?? 0) / columnWidth)
  const { loading, error } = useListContext()
  if (loading || error) return null
  return (
    <div
      {...rest}
      ref={$container}
      style={{
        display: 'grid',
        columnGap,
        rowGap,
        gridTemplateColumns: `repeat(${actualCols},1fr)`,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

function Loading({
  asChild,
  columnWidth = 268,
  source = '',
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { asChild?: boolean; columnWidth?: number; source?: string }) {
  const { loading } = useListContext()
  const Com = asChild ? Slot : CardSkeleton
  if (!loading) return null
  return <Com {...rest} columnWidth={columnWidth} source={source} />
  // return <CardSkeleton num={32} />
}

function Error({ asChild, ...rest }: React.ComponentProps<typeof ErrorCard> & { asChild?: boolean }) {
  const { loading, error } = useListContext()
  if (loading || !error) return null
  const Com = asChild ? Slot : ErrorCard
  return <Com {...rest} />
}

function Empty({ asChild, className, ...rest }: React.ComponentProps<typeof EmptyCard> & { asChild?: boolean }) {
  const { loading, error, empty } = useListContext()
  if (loading || error || !empty) return null
  const Com = asChild ? Slot : EmptyCard
  return <Com {...rest} className={cn('h-full flex items-center justify-center', className)} />
}

/** 统一List中卡片的基础样式 */

function Footer({ children, className, asChild, ...rest }: React.HTMLAttributes<HTMLElement> & { asChild?: boolean }) {
  const { loading, error, empty } = useListContext()
  const Com = asChild ? Slot : 'div'
  if (loading || error || empty) return null
  return (
    <Com className={cn('flex items-center justify-center', className)} {...rest}>
      {children}
    </Com>
  )
}
export { Root, ViewBox, Loading, Error, Empty, Footer }
